import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDropzone } from 'react-dropzone';
import { X, Camera, Video } from 'lucide-react';
import axios from 'axios';
import { toast } from 'react-toastify';
import config from '../config';

const AddCar = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    licensePlate: '',
    description: '',
    location: ''
  });
  const [photos, setPhotos] = useState([]);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [captcha, setCaptcha] = useState({ captchaId: '', question: '' });
  const [captchaAnswer, setCaptchaAnswer] = useState('');

  const loadCaptcha = async () => {
    try {
      const { data } = await axios.get(`${config.API_URL}/api/captcha`);
      setCaptcha({ captchaId: data.captchaId, question: data.question });
      setCaptchaAnswer('');
    } catch (err) {
      console.error('Captcha load failed', err);
    }
  };

  // Очищаем сохраненную позицию скролла главной страницы
  useEffect(() => {
    sessionStorage.removeItem('homeScrollPosition');
    loadCaptcha();
  }, []);

  const onPhotoDrop = (acceptedFiles) => {
    const newPhotos = acceptedFiles.map(file => ({
      file,
      preview: URL.createObjectURL(file),
      id: Math.random().toString(36).substr(2, 9)
    }));
    setPhotos(prev => [...prev, ...newPhotos]);
  };

  const onVideoDrop = (acceptedFiles) => {
    const newVideos = acceptedFiles.map(file => ({
      file,
      preview: URL.createObjectURL(file),
      id: Math.random().toString(36).substr(2, 9)
    }));
    setVideos(prev => [...prev, ...newVideos]);
  };

  const removePhoto = (id) => {
    setPhotos(prev => {
      const photo = prev.find(p => p.id === id);
      if (photo) {
        URL.revokeObjectURL(photo.preview);
      }
      return prev.filter(p => p.id !== id);
    });
  };

  const removeVideo = (id) => {
    setVideos(prev => {
      const video = prev.find(v => v.id === id);
      if (video) {
        URL.revokeObjectURL(video.preview);
      }
      return prev.filter(v => v.id !== id);
    });
  };

  const { getRootProps: getPhotoRootProps, getInputProps: getPhotoInputProps, isDragActive: isPhotoDragActive } = useDropzone({
    onDrop: onPhotoDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.gif']
    },
    maxSize: 50 * 1024 * 1024 // 50MB
  });

  const { getRootProps: getVideoRootProps, getInputProps: getVideoInputProps, isDragActive: isVideoDragActive } = useDropzone({
    onDrop: onVideoDrop,
    accept: {
      'video/*': ['.mp4', '.mov', '.avi']
    },
    maxSize: 100 * 1024 * 1024 // 100MB
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.licensePlate.trim()) {
      toast.error(t('addCar.error'));
      return;
    }

    if (photos.length === 0 && videos.length === 0) {
      toast.error(t('addCar.error'));
      return;
    }

    if (!captchaAnswer.trim()) {
      toast.error('Решите пример (защита от ботов)');
      return;
    }

    setLoading(true);

    try {
      const submitData = new FormData();
      submitData.append('licensePlate', formData.licensePlate);
      submitData.append('description', formData.description);
      submitData.append('captchaId', captcha.captchaId);
      submitData.append('captchaAnswer', captchaAnswer.trim());
      // Honeypot — must stay empty
      submitData.append('website', '');
      
      if (formData.location) {
        submitData.append('location', JSON.stringify({ address: formData.location }));
      }

      photos.forEach(photo => {
        submitData.append('photos', photo.file);
      });

      videos.forEach(video => {
        submitData.append('videos', video.file);
      });

      await axios.post(`${config.API_URL}/api/cars`, submitData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success(t('addCar.success'));
      navigate('/');
    } catch (error) {
      console.error('Ошибка при добавлении записи:', error);
      toast.error(error.response?.data?.error || t('addCar.error'));
      loadCaptcha();
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  return (
    <div className="container page-shell">
      <div style={{ maxWidth: '720px' }}>
        <h1 className="page-shell__title">{t('addCar.title')}</h1>
        <p className="page-shell__lead">{t('addCar.subtitle')}</p>

        <form onSubmit={handleSubmit} className="form-panel">
            <div className="form-group">
              <label className="form-label">
                {t('addCar.licensePlate')}
              </label>
              <input
                type="text"
                name="licensePlate"
                value={formData.licensePlate}
                onChange={handleInputChange}
                className="form-input"
                placeholder={t('addCar.licensePlatePlaceholder')}
                required
                style={{ textTransform: 'uppercase', fontFamily: 'var(--font-plate)', letterSpacing: '0.08em' }}
              />
            </div>

            <div className="form-group">
              <label className="form-label">
                {t('addCar.description')}
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                className="form-input"
                placeholder={t('addCar.descriptionPlaceholder')}
                rows="3"
                style={{ resize: 'vertical' }}
              />
            </div>

            <div className="form-group">
              <label className="form-label">
                {t('addCar.location')}
              </label>
              <input
                type="text"
                name="location"
                value={formData.location}
                onChange={handleInputChange}
                className="form-input"
                placeholder={t('addCar.locationPlaceholder')}
              />
            </div>

            <div className="form-group">
              <label className="form-label">
                {t('addCar.photos')}
              </label>
              <div
                {...getPhotoRootProps()}
                className={`dropzone${isPhotoDragActive ? ' is-active' : ''}`}
              >
                <input {...getPhotoInputProps()} />
                <Camera size={28} style={{ color: 'var(--signal-deep)', marginBottom: 8 }} />
                <p style={{ margin: '8px 0', color: 'var(--slate)', fontWeight: 600 }}>
                  {isPhotoDragActive ? (t('addCar.dropFiles') || t('addCar.dragPhotos')) : t('addCar.dragPhotos')}
                </p>
                <p style={{ fontSize: 12, color: 'var(--mute)' }}>
                  {t('addCar.photoFormats')}
                </p>
              </div>

              {photos.length > 0 && (
                <div style={{ marginTop: '16px' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: '8px' }}>
                    {photos.map((photo) => (
                      <div key={photo.id} style={{ position: 'relative' }}>
                        <img
                          src={photo.preview}
                          alt="Preview"
                          style={{
                            width: '100%',
                            height: '120px',
                            objectFit: 'cover',
                            borderRadius: '8px'
                          }}
                        />
                        <button
                          type="button"
                          onClick={() => removePhoto(photo.id)}
                          style={{
                            position: 'absolute',
                            top: '4px',
                            right: '4px',
                            background: 'rgba(0,0,0,0.7)',
                            color: 'white',
                            border: 'none',
                            borderRadius: '50%',
                            width: '24px',
                            height: '24px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          <X size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">
                {t('addCar.videos')}
              </label>
              <div
                {...getVideoRootProps()}
                className={`dropzone${isVideoDragActive ? ' is-active' : ''}`}
              >
                <input {...getVideoInputProps()} />
                <Video size={28} style={{ color: 'var(--signal-deep)', marginBottom: 8 }} />
                <p style={{ margin: '8px 0', color: 'var(--slate)', fontWeight: 600 }}>
                  {isVideoDragActive ? (t('addCar.dropFiles') || t('addCar.dragVideos')) : t('addCar.dragVideos')}
                </p>
                <p style={{ fontSize: 12, color: 'var(--mute)' }}>
                  {t('addCar.videoFormats')}
                </p>
              </div>

              {videos.length > 0 && (
                <div style={{ marginTop: 16 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 8 }}>
                    {videos.map((video) => (
                      <div key={video.id} style={{ position: 'relative' }}>
                        <video
                          src={video.preview}
                          style={{
                            width: '100%',
                            height: 120,
                            objectFit: 'cover',
                            borderRadius: 4
                          }}
                          controls
                        />
                        <button
                          type="button"
                          onClick={() => removeVideo(video.id)}
                          className="btn btn-danger"
                          style={{
                            position: 'absolute',
                            top: 4,
                            right: 4,
                            padding: 4,
                            minWidth: 28
                          }}
                        >
                          <X size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">Защита от ботов</label>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                <span style={{ fontWeight: 700, fontSize: 16, minWidth: 90 }}>
                  {captcha.question || '…'}
                </span>
                <input
                  type="text"
                  inputMode="numeric"
                  value={captchaAnswer}
                  onChange={(e) => setCaptchaAnswer(e.target.value)}
                  className="form-input"
                  placeholder="Ответ"
                  required
                  autoComplete="off"
                  style={{ maxWidth: 120 }}
                />
                <button type="button" className="btn btn-secondary" onClick={loadCaptcha}>
                  Обновить
                </button>
              </div>
              {/* Honeypot for bots */}
              <input
                type="text"
                name="website"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                style={{ position: 'absolute', left: '-9999px', height: 0, width: 0, opacity: 0 }}
              />
            </div>

            <div style={{ display: 'flex', gap: 12, marginTop: 24 }}>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={loading}
                style={{ flex: 1 }}
              >
                {loading ? (t('addCar.submitting') || t('addCar.saveRecord')) : t('addCar.saveRecord')}
              </button>
              <button
                type="button"
                onClick={() => navigate('/')}
                className="btn btn-secondary"
                style={{ flex: 1 }}
              >
                {t('addCar.cancel')}
              </button>
            </div>
        </form>
      </div>
    </div>
  );
};

export default AddCar;
